import { useResourceDefinitions } from "ra-core";

import { useContactImport } from "../contacts/useContactImport";
import { useCompanyColumns } from "../spreadsheet/companyColumns";
import { useContactColumns } from "../spreadsheet/contactColumns";
import { useDealColumns } from "../spreadsheet/dealColumns";
import type { ImportableResource } from "./types";
import { useCompanyImport } from "./useCompanyImport";
import { useDealImport } from "./useDealImport";

/** Every resource a file can be imported into, in the order the dialog offers them. */
export const IMPORTABLE_RESOURCES = ["contacts", "companies", "deals"] as const;

export type ImportableResourceName = (typeof IMPORTABLE_RESOURCES)[number];

/**
 * The importable resources, restricted to those the running Admin registers:
 * the mobile app has no deals screens, so it must not offer to import records
 * the user would never be able to see.
 */
export function useImportableResources(): ImportableResource[] {
  const processContacts = useContactImport();
  const processCompanies = useCompanyImport();
  const processDeals = useDealImport();
  const definitions = useResourceDefinitions();
  const contactColumns = useContactColumns();
  const companyColumns = useCompanyColumns();
  const dealColumns = useDealColumns();

  const resources: ImportableResource[] = [
    {
      name: "contacts",
      columns: contactColumns,
      textColumns: ["phone_work", "phone_home", "phone_other"],
      processBatch: processContacts,
    },
    {
      name: "companies",
      columns: companyColumns,
      // These are text columns whose leading zero must survive the parsing:
      // a "02134" zipcode would otherwise be stored as 2134.
      textColumns: ["zipcode", "phone_number", "tax_identifier"],
      processBatch: processCompanies,
    },
    { name: "deals", columns: dealColumns, processBatch: processDeals },
  ];

  return resources.filter(({ name }) => name in definitions);
}
